import type { JSX } from "react";
import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { useSceneStore } from "@/state/useSceneStore";

const PULSE_PERIOD = 4;
const PULSE_AMPLITUDE = 0.15;

interface LightDef {
  offset: [number, number, number];
  color: number;
  intensity: number;
  distance: number;
  phase: number;
}

const FALLBACK_CPU: [number, number, number] = [0.015, 0.13, -0.281];

const LIGHTS: LightDef[] = [
  {
    offset: [-0.2031, 0.1714, -0.0089],
    color: 0xff0000,
    intensity: 8,
    distance: 0.09,
    phase: 0.0,
  },
  {
    offset: [0.1832, 0.1575, -0.0089],
    color: 0x00ff00,
    intensity: 6,
    distance: 0.07,
    phase: 0.6,
  },
  {
    offset: [0.1832, 0.0526, -0.0089],
    color: 0x00ff00,
    intensity: 6,
    distance: 0.07,
    phase: 1.2,
  },
  {
    offset: [0.1832, -0.0521, -0.0089],
    color: 0x00ff00,
    intensity: 6,
    distance: 0.07,
    phase: 1.8,
  },
  {
    offset: [-0.0503, 0.152, -0.025],
    color: 0x00ff00,
    intensity: 6,
    distance: 0.07,
    phase: 2.4,
  },
];

export const FanLights = (): JSX.Element => {
  const cpuMesh = useSceneStore((s) => s.objects.cpu);
  const refs = useRef<(THREE.PointLight | null)[]>([]);

  const positions = useMemo<[number, number, number][]>(() => {
    const base = new THREE.Vector3();
    if (cpuMesh) {
      cpuMesh.updateWorldMatrix(true, false);
      const m = cpuMesh.matrixWorld.elements;
      base.set(m[12] ?? 0, m[13] ?? 0, m[14] ?? 0);
    } else {
      base.set(...FALLBACK_CPU);
    }
    return LIGHTS.map((spec) => [
      base.x + spec.offset[0],
      base.y + spec.offset[1],
      base.z + spec.offset[2],
    ]);
  }, [cpuMesh]);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    for (let i = 0; i < LIGHTS.length; i++) {
      const light = refs.current[i];
      if (!light) continue;
      const spec = LIGHTS[i];
      if (!spec) continue;
      const wave = Math.sin(((t + spec.phase) * 2 * Math.PI) / PULSE_PERIOD);
      light.intensity = spec.intensity * (1 + PULSE_AMPLITUDE * wave);
    }
  });

  return (
    <>
      {LIGHTS.map((spec, i) => {
        const pos = positions[i] ?? [0, 0, 0];
        return (
          <pointLight
            key={i}
            ref={(el) => {
              refs.current[i] = el;
            }}
            position={pos}
            color={spec.color}
            intensity={spec.intensity}
            distance={spec.distance}
          />
        );
      })}
    </>
  );
};
